import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { api } from "@/lib/api";

// Worker bundled via CDN (unpkg) — robusto en producción con CRA.
// El método `new URL(...)` daba "Failed to fetch dynamically imported module" en build optimizado.
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Full-screen viewer for an uploaded project document.
 * Supports PDF (react-pdf), images (img tag), and plain text (pre).
 */
export default function DocumentViewer({ projectId, doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.25);

  const filename = doc?.filename || "documento";
  const lower = filename.toLowerCase();
  const isPdf = lower.endsWith(".pdf") || doc?.mime === "application/pdf";
  const isImage = /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(lower) ||
    (doc?.mime || "").startsWith("image/");
  const isText = /\.(txt|fountain|fdx|md|csv|json|xml)$/i.test(lower) ||
    (doc?.mime || "").startsWith("text/");

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await api.get(
          `/icaa/projects/${projectId}/documents/${doc.id}/file`,
          { responseType: "blob" }
        );
        if (cancelled) return;
        const mime = doc.mime || (isPdf ? "application/pdf" : "application/octet-stream");
        const blob = new Blob([r.data], { type: mime });
        if (isText) {
          const txt = await blob.text();
          if (!cancelled) setTextContent(txt);
        } else {
          createdUrl = URL.createObjectURL(blob);
          if (!cancelled) setBlobUrl(createdUrl);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [projectId, doc?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const openInNewTab = () => {
    if (blobUrl) window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#2C2B29]" data-testid="document-viewer-modal">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-[#2C2B29] px-4 py-3 text-white sm:px-6">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#941620]">{doc?.title || "Documento"}</p>
          <p className="mt-0.5 truncate text-sm font-semibold">{filename}</p>
          {(numPages > 0 || doc?.size) && (
            <p className="text-[11px] text-white/55">
              {numPages > 0 && <>{numPages} página{numPages === 1 ? "" : "s"} · </>}
              {doc?.size > 0 && <>{(doc.size / 1024).toFixed(1)} KB</>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isPdf && blobUrl && (
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 px-1 py-1 text-white" data-testid="docviewer-zoom">
              <button onClick={() => setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)))}
                      data-testid="docviewer-zoom-out"
                      className="rounded-full px-2 py-0.5 text-xs hover:bg-white/15">−</button>
              <span className="min-w-[42px] text-center text-[11px] tabular-nums">{Math.round(scale * 100)}%</span>
              <button onClick={() => setScale((s) => Math.min(3, +(s + 0.15).toFixed(2)))}
                      data-testid="docviewer-zoom-in"
                      className="rounded-full px-2 py-0.5 text-xs hover:bg-white/15">+</button>
            </div>
          )}
          {blobUrl && (
            <button onClick={openInNewTab} data-testid="docviewer-open-tab"
                    className="rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/20">
              ⇱ Abrir en pestaña
            </button>
          )}
          <button onClick={download} data-testid="docviewer-download" disabled={!blobUrl}
                  className="rounded-full bg-[#26775E] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
            ↓ Descargar
          </button>
          <button onClick={onClose} data-testid="docviewer-close"
                  className="rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/20">
            ✕ Cerrar
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-[#33333f] py-6">
        {loading && (
          <div className="flex h-full items-center justify-center text-white/70" data-testid="docviewer-loading">
            <p className="text-sm uppercase tracking-[0.3em]">Cargando documento…</p>
          </div>
        )}
        {!loading && err && (
          <div className="flex h-full items-center justify-center p-6">
            <p className="rounded-md bg-red-500/20 px-4 py-3 text-sm text-red-100">{err}</p>
          </div>
        )}

        {!loading && !err && isPdf && blobUrl && (
          <div className="mx-auto flex w-fit flex-col items-center gap-6" data-testid="docviewer-pdf-pages">
            <Document
              file={blobUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => setErr(e?.message || "Error al cargar el PDF")}
              loading={<p className="text-sm text-white/60">Cargando páginas…</p>}>
              {Array.from({ length: numPages }, (_, i) => (
                <div key={`p-${i + 1}`} className="mb-5 shadow-2xl">
                  <Page pageNumber={i + 1} scale={scale}
                        renderTextLayer={true} renderAnnotationLayer={false} />
                </div>
              ))}
            </Document>
          </div>
        )}

        {!loading && !err && isImage && blobUrl && (
          <div className="flex items-center justify-center p-4" data-testid="docviewer-image">
            <img src={blobUrl} alt={doc?.title || filename}
                 className="max-h-full max-w-full rounded-lg bg-white shadow-2xl" />
          </div>
        )}

        {!loading && !err && isText && (
          <div className="px-4 sm:px-8">
            <pre className="mx-auto max-w-3xl rounded-lg bg-[#FBFAF4] p-6 font-mono text-[13px] leading-relaxed text-stone-800 shadow-2xl"
                 style={{ whiteSpace: "pre-wrap" }}
                 data-testid="docviewer-text">{textContent}</pre>
          </div>
        )}

        {!loading && !err && !isPdf && !isImage && !isText && (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div>
              <p className="text-2xl text-white/80">Vista previa no disponible</p>
              <p className="mt-2 text-sm text-white/55">
                Este tipo de archivo no se puede previsualizar. Pulsa "Descargar".
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
